// =============================================
// Services Types
// =============================================

export type Service = {
  id: string;
  name: string;
  description: string | null;
  default_price: number | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ServiceFormData = {
  name: string;
  description?: string | null;
  default_price?: number | null;
  is_active?: boolean;
  sort_order?: number;
};

// =============================================
// Enhanced Payment Line Item Types (with discounts)
// =============================================

export type DiscountType = 'percentage' | 'fixed';

export type PaymentLineItemFormData = {
  id?: string; // For tracking in UI before save
  item_type: 'service' | 'supplement' | 'other';
  service_id?: string | null;
  product_id?: string | null;
  client_supplement_id?: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  discount_type?: DiscountType | null;
  discount_value?: number;
  discount_reason?: string | null;
};

export type PaymentLineItemWithCalculations = PaymentLineItemFormData & {
  gross_amount: number;
  discount_amount: number;
  net_amount: number;
};

// =============================================
// Enhanced Payment Types (with discounts and tax)
// =============================================

export type PaymentInputMode = 'quick' | 'detailed';

export type EnhancedPaymentFormData = {
  input_mode: PaymentInputMode;

  // Quick mode fields
  amount?: number;

  // Detailed mode fields
  line_items?: PaymentLineItemFormData[];
  subtotal?: number;
  overall_discount_type?: DiscountType | null;
  overall_discount_value?: number;
  overall_discount_reason?: string | null;
  overall_discount_amount?: number;
  tax_enabled?: boolean;
  tax_rate?: number;
  tax_amount?: number;

  // Common fields
  payment_method?: string | null;
  status: 'pending' | 'paid' | 'expired';
  due_at: string;
  paid_at?: string | null;
  notes?: string | null;
  transaction_id?: string | null;
};

// =============================================
// Calculation Helpers
// =============================================

export function calculateLineItemDiscount(
  grossAmount: number,
  discountType: DiscountType | null | undefined,
  discountValue: number | undefined
): number {
  if (!discountType || !discountValue || discountValue <= 0) {
    return 0;
  }

  if (discountType === 'percentage') {
    return Math.round((grossAmount * discountValue / 100) * 100) / 100;
  }

  // Fixed discount - can't exceed gross amount
  return Math.min(discountValue, grossAmount);
}

export function calculateLineItemNetAmount(
  quantity: number,
  unitPrice: number,
  discountType: DiscountType | null | undefined,
  discountValue: number | undefined
): { grossAmount: number; discountAmount: number; netAmount: number } {
  const grossAmount = Math.round(quantity * unitPrice * 100) / 100;
  const discountAmount = calculateLineItemDiscount(grossAmount, discountType, discountValue);
  const netAmount = Math.round((grossAmount - discountAmount) * 100) / 100;

  return { grossAmount, discountAmount, netAmount };
}

export function calculateOverallDiscount(
  subtotal: number,
  discountType: DiscountType | null | undefined,
  discountValue: number | undefined
): number {
  if (!discountType || !discountValue || discountValue <= 0) {
    return 0;
  }

  if (discountType === 'percentage') {
    return Math.round((subtotal * discountValue / 100) * 100) / 100;
  }

  // Fixed discount - can't exceed subtotal
  return Math.min(discountValue, subtotal);
}

export function calculatePaymentTotals(
  lineItems: PaymentLineItemWithCalculations[],
  overallDiscountType: DiscountType | null | undefined,
  overallDiscountValue: number | undefined,
  taxEnabled: boolean,
  taxRate: number
): {
  subtotal: number;
  overallDiscountAmount: number;
  taxableAmount: number;
  taxAmount: number;
  grandTotal: number;
} {
  const subtotal = lineItems.reduce((sum, item) => sum + item.net_amount, 0);
  const overallDiscountAmount = calculateOverallDiscount(subtotal, overallDiscountType, overallDiscountValue);
  const taxableAmount = Math.round((subtotal - overallDiscountAmount) * 100) / 100;
  const taxAmount = taxEnabled ? Math.round((taxableAmount * taxRate / 100) * 100) / 100 : 0;
  const grandTotal = Math.round((taxableAmount + taxAmount) * 100) / 100;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    overallDiscountAmount,
    taxableAmount,
    taxAmount,
    grandTotal,
  };
}

export function formatCurrency(amount: number, currency: string = 'INR'): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}
